// Link text for review & submit page errors
// key = "name" from `form.formErrors.errors`
// see src/platform/forms-system/docs/reviewErrors.md
import {
  isServingComponentGuardAndReserves,
  isMilitaryRetiredPay,
  isTrainingPay,
} from './utils/pageDependencies';

const SUPPORTING_EVIDENCE = 'supportingEvidence';
const ADDITIONAL_INFORMATION = 'additionalInformation';

// Service pay errors are matched by field name and pinned to the
// additionalInformation chapter so they can't fall through to the generic
// page search. The parent `servicePay` object is required by the waiver pages.
const SERVICE_PAY_FIELD_PAGES = {
  'view:servicePayType': 'servicePayType',
  isReceivingServicePay: 'servicePay',
  isWaivingVaBenefitsForMilitaryRetiredPay: 'waiverMilitaryRetiredPay',
  isWaivingVaBenefitsForTrainingPay: 'waiverTrainingPay',
};

const getServicePayPage = (error, context) => {
  const field = Object.keys(SERVICE_PAY_FIELD_PAGES).find(
    key => error.includes(key) || context?.argument === key,
  );
  if (field) return SERVICE_PAY_FIELD_PAGES[field];

  if (context?.argument === 'servicePay') {
    if (isMilitaryRetiredPay(context.formData)) {
      return 'waiverMilitaryRetiredPay';
    }
    if (isTrainingPay(context.formData)) return 'waiverTrainingPay';
    return 'servicePay';
  }
  return null;
};

const redirectTo = (chapterKey, pageKey) => ({
  chapterKey,
  pageKey,
  navigationType: 'redirect',
});

// Required-field errors report only the leaf property name (e.g.
// `treatmentCenterName`), so loop fields are matched by exact name
const LOOP_FIELD_PAGES = {
  'view:hasVaTreatmentFacilities': 'vaMedicalRecordsFacilitySummary',
  vaTreatmentFacilities: 'vaMedicalRecordsFacilitySummary',
  'view:vaTreatmentFacilitiesValidation': 'vaMedicalRecordsFacilitySummary',
  treatmentCenterName: 'vaMedicalRecordsFacilitySummary',
  treatmentCenterAddress: 'vaMedicalRecordsFacilitySummary',
  treatedDisabilityNames: 'vaMedicalRecordsFacilitySummary',
  'view:hasProviderFacilities': 'privateMedicalRecordsReleaseSummary',
  providerFacility: 'privateMedicalRecordsReleaseSummary',
  'view:providerFacilityValidation': 'privateMedicalRecordsReleaseSummary',
  providerFacilityName: 'privateMedicalRecordsReleaseSummary',
  providerFacilityAddress: 'privateMedicalRecordsReleaseSummary',
  'view:hasAnotherPrivateMedicalRecord': 'privateMedicalRecordsUploadSummary',
  privateMedicalRecordsUploads: 'privateMedicalRecordsUploadSummary',
  'view:privateMedicalRecordsUploadsValidation':
    'privateMedicalRecordsUploadSummary',
  'view:hasAnotherSupportingDocument': 'additionalEvidenceSummary',
  additionalDocumentsLoop: 'additionalEvidenceSummary',
  'view:additionalDocumentsLoopValidation': 'additionalEvidenceSummary',
};

// "Required" errors on loop items reach `_override` as the item path (e.g.
// `vaTreatmentFacilities[0]`). Item pages have no review form and their paths
// contain `:index`, so those errors redirect to the loop's summary page.
const LOOP_SUMMARY_PAGES = {
  vaTreatmentFacilities: 'vaMedicalRecordsFacilitySummary',
  providerFacility: 'privateMedicalRecordsReleaseSummary',
  privateMedicalRecordsUploads: 'privateMedicalRecordsUploadSummary',
  additionalDocumentsLoop: 'additionalEvidenceSummary',
};

const CONDITIONS_ERROR =
  /^(instance\.)?newDisabilities$|^condition$|^view:hasConditions$|^view:newDisabilitiesValidation$|newDisabilities.*does not meet minimum length|newDisabilities\[\d+\]\.condition/;

// `active-service-dates` is hidden on review, so its errors redirect to the page
const ACTIVE_SERVICE_DATE_FIELDS = ['entryDate', 'exitDate', 'servicePeriods'];

// Bank account fields are edited on the paymentInformationReview card, and
// paymentInformation is hidden for prefilled users or renders no review UI
const BANK_ACCOUNT_ERROR_KEYS = [
  'directDeposit',
  'accountType',
  'accountNumber',
  'routingNumber',
  'bankName',
];

export default {
  primaryPhone: 'Contact information (enter your phone number)',
  emailAddress: 'Contact information (enter your email address)',
  addressLine1: 'Contact information (enter a street address)',
  zipCode:
    'Contact information (enter a valid postal code for your mailing address)',
  isCurrentlyHomelessOrIsAtRiskOfBecomingHomeless:
    'Are you homeless or at risk of becoming homeless? (select one of the answers)',
  isTerminallyIll: 'Are you terminally ill? (select yes or no)',
  serviceInformation:
    'Military service information (fill in any missing information)',
  serviceBranches:
    'Military service history (select at least one branch of service)',
  serviceComponent: 'Component served (select at least one component)',
  component: 'Federal orders (select the component you serve in)',
  reservesNationalGuardService:
    'Reserve or National Guard service (finish your service details)',
  currentlyActivated:
    'Current activation (select yes or no for your current federal activation)',
  federalActivation:
    'Current activation (select yes or no for your current federal activation)',
  obligationTermsOfService:
    'Obligation term of service (enter your obligation dates)',
  anticipatedSeparationDate:
    'Federal activation separation date (enter your anticipated separation date)',
  beginDate: 'Obligation term of service (enter your obligation start date)',
  endDate: 'Obligation term of service (enter your obligation end date)',
  entryDate: 'Most recent active service dates (enter your service start date)',
  exitDate: 'Most recent active service dates (enter your service end date)',
  servicePeriods:
    'Most recent active service dates (fill in any missing information for your service start and end dates)',
  powStatus: 'Are you a former POW? (select yes or no)',
  powEraCampaign: 'POW era or campaign (select at least one option)',
  powConfinement: 'Period of time held as a POW (enter your dates)',
  // `from` and `to` are shared by every date range, so these keys are qualified
  'powConfinement.from': 'Period of time held as a POW (enter a start date)',
  'powConfinement.to': 'Period of time held as a POW (enter an end date)',
  // `city`, `state` and `country` are shared by several addresses, so they use
  // path-qualified keys
  'mailingAddress.city':
    'Contact information (enter a city for your mailing address)',
  'mailingAddress.state':
    'Contact information (enter a state for your mailing address)',
  'mailingAddress.country':
    'Contact information (select a country for your mailing address)',
  // Loop items repeat these fields, so the link text includes the item number
  // to keep each link unique for screen reader users
  'vaTreatmentFacilities.treatmentCenterAddress.city': index =>
    `VA or military health facilities (enter the city for facility ${
      index + 1
    })`,
  'vaTreatmentFacilities.treatmentCenterAddress.state': index =>
    `VA or military health facilities (enter the state for facility ${
      index + 1
    })`,
  'vaTreatmentFacilities.treatmentCenterAddress.country': index =>
    `VA or military health facilities (select the country for facility ${
      index + 1
    })`,
  'providerFacility.providerFacilityAddress.city': index =>
    `Private healthcare providers (enter the city for provider ${index + 1})`,
  'providerFacility.providerFacilityAddress.state': index =>
    `Private healthcare providers (enter the state for provider ${index + 1})`,
  'providerFacility.providerFacilityAddress.country': index =>
    `Private healthcare providers (select the country for provider ${
      index + 1
    })`,
  newDisabilities: 'Add at least one condition to your claim',
  'view:hasMedicalRecords':
    'Medical records that support your disability claim (select yes or no)',
  'view:selectableEvidenceTypes':
    'Supporting documents and additional forms for your disability claim (select yes or no)',
  'view:hasOtherEvidence':
    'Supporting documents and additional forms for your disability claim (select yes or no)',
  'view:hasConditions': 'Add at least one condition to your claim',
  condition:
    'Add new condition (enter a condition, diagnosis, or short description of your symptoms)',
  cause: 'What caused your condition? (select one of the answers)',
  // The array builder adds these hidden `view:<arrayPath>Validation` errors
  // with an empty message when a loop item is incomplete. They can be the only
  // error blocking submit, so they need link text rather than suppression.
  'view:newDisabilitiesValidation':
    'Conditions (finish or remove any incomplete conditions)',
  'view:hasVaTreatmentFacilities':
    'VA or military health facilities (select yes or no)',
  treatmentCenterName: index =>
    `VA or military health facilities (enter the name for facility ${
      index + 1
    })`,
  treatmentCenterAddress: index =>
    `VA or military health facilities (enter the location for facility ${
      index + 1
    })`,
  treatedDisabilityNames: index =>
    `VA or military health facilities (select at least one condition for facility ${
      index + 1
    })`,
  patient4142Acknowledgement:
    'Private medical records (authorize the release of your records)',
  'view:limitedConsent':
    'Private medical records (select yes or no to limit your consent)',
  limitedConsent:
    'Private medical records (describe how you want to limit your consent)',
  'view:hasProviderFacilities':
    'Private healthcare providers (select yes or no)',
  providerFacilityName: index =>
    `Private healthcare providers (enter the name for provider ${index + 1})`,
  providerFacilityAddress: index =>
    `Private healthcare providers (enter the address for provider ${
      index + 1
    })`,
  'view:privateMedicalRecordsAccess':
    'Select how you would like us to access your records from other healthcare providers',
  'view:hasAnotherPrivateMedicalRecord':
    'Private medical records (select yes or no)',
  'view:hasAnotherSupportingDocument':
    'Supporting documents and additional forms (select yes or no)',
  uploadedDocument: 'Supporting evidence (upload a file)',
  attachmentId: 'Supporting evidence (upload a file)',
  vaTreatmentFacilities:
    'VA or military health facilities (add at least one facility)',
  'view:vaTreatmentFacilitiesValidation':
    'VA or military health facilities (finish or remove any incomplete facilities)',
  providerFacility: 'Private healthcare providers (add at least one provider)',
  'view:providerFacilityValidation':
    'Private healthcare providers (finish or remove any incomplete providers)',
  privateMedicalRecordsUploads:
    'Private medical records (upload at least one file)',
  'view:privateMedicalRecordsUploadsValidation':
    'Private medical records (finish or remove any incomplete records)',
  additionalDocumentsLoop:
    'Supporting documents and additional forms (upload at least one file)',
  'view:additionalDocumentsLoopValidation':
    'Supporting documents and additional forms (finish or remove any incomplete documents)',
  'view:hasSeparationHealthAssessment':
    'Separation Health Assessment (select yes or no)',
  separationHealthAssessmentUploads: 'Upload your Separation Health Assessment',
  'view:hasServiceTreatmentRecords':
    'Service treatment records (select yes or no)',
  mentalHealthWorkflowChoice:
    'Mental health statement (choose how you want to provide your statement)',
  answerCombatBehaviorQuestions:
    'Mental health statement (select yes or no for the behavior change questions)',
  form781Upload:
    'Mental health statement (upload your completed paper statement)',
  hasNoAccount:
    'Direct deposit (select whether you have a bank account to receive payments)',
  accountType: 'Direct deposit (select an account type)',
  'directDeposit.accountType': 'Direct deposit (select an account type)',
  'directDeposit.accountNumber':
    'Direct deposit (enter a valid account number with 5 to 17 letters or numbers)',
  'directDeposit.routingNumber':
    'Direct deposit (enter a valid 9-digit routing number)',
  bankName: 'Direct deposit (enter your bank name)',
  'directDeposit.bankName': 'Direct deposit (enter your bank name)',
  accountNumber: 'Direct deposit (enter your account number)',
  routingNumber: 'Direct deposit (enter your routing number)',
  isReceivingServicePay:
    'Are you receiving military retired pay or training pay? (select yes or no)',
  'view:servicePayType': 'Type of service pay (select at least one option)',
  isWaivingVaBenefitsForMilitaryRetiredPay:
    'Do you want to waive military retired pay to receive VA compensation? (select yes or no)',
  isWaivingVaBenefitsForTrainingPay:
    'Do you want to waive training pay to receive VA compensation? (select yes or no)',
  // Reported when an unanswered waiver page leaves the parent object missing
  servicePay:
    'Waiver of military retired pay or training pay (select whether you want to receive VA disability compensation)',
  _override: (error, context = {}) => {
    if (typeof error !== 'string') {
      return null;
    }

    // Empty conditions list, or an invalid condition name: send the Veteran to
    // the conditions summary, where the cards and "incomplete" labels live.
    if (CONDITIONS_ERROR.test(error)) {
      return redirectTo('conditions', 'Summary');
    }

    // Missing `condition` or `cause` on a condition item reports the item
    // (e.g. `newDisabilities[1]`) as the error and the field as the argument.
    if (
      /^newDisabilities\[\d+\]$/.test(error) &&
      ['condition', 'cause'].includes(context?.argument)
    ) {
      return redirectTo('conditions', 'Summary');
    }

    const loopPath = error.match(/^([^[.]+)\[\d+\]/)?.[1];
    if (LOOP_SUMMARY_PAGES[loopPath]) {
      return redirectTo(SUPPORTING_EVIDENCE, LOOP_SUMMARY_PAGES[loopPath]);
    }

    if (LOOP_FIELD_PAGES[error]) {
      return {
        chapterKey: SUPPORTING_EVIDENCE,
        pageKey: LOOP_FIELD_PAGES[error],
      };
    }

    // Pages hidden on the review page: redirect to them instead.
    if (
      ACTIVE_SERVICE_DATE_FIELDS.includes(error) ||
      error.includes('servicePeriods') ||
      context?.argument === 'servicePeriods'
    ) {
      return redirectTo('veteranDetails', 'mostRecentService');
    }
    if (error.includes('view:hasMedicalRecords')) {
      return redirectTo(SUPPORTING_EVIDENCE, 'evidenceRequest');
    }
    if (
      error.includes('view:hasOtherEvidence') ||
      context?.argument === 'view:hasOtherEvidence'
    ) {
      return redirectTo(SUPPORTING_EVIDENCE, 'additionalEvidenceIntro');
    }
    // The parent object is required by the additional evidence question; the
    // record types page uses `view:medicalRecordTypes` instead.
    if (error.includes('view:selectableEvidenceTypes')) {
      return redirectTo(SUPPORTING_EVIDENCE, 'additionalEvidenceIntro');
    }

    // These pages have no review form, so redirect to them. Required errors
    // reach `_override` as the validator stack, not the bare field name.
    if (error.includes('view:hasSeparationHealthAssessment')) {
      return redirectTo(SUPPORTING_EVIDENCE, 'separationHealthAssessment');
    }
    if (error.includes('view:hasServiceTreatmentRecords')) {
      return redirectTo(SUPPORTING_EVIDENCE, 'serviceTreatmentRecords');
    }
    if (error.includes('patient4142Acknowledgement')) {
      return redirectTo(
        SUPPORTING_EVIDENCE,
        'privateMedicalRecordsAuthorizeRelease',
      );
    }

    // The parent object is required by both pages; `federalOrders` is only
    // active when the Veteran served in both the Reserves and National Guard.
    if (
      error.includes('reservesNationalGuardService') ||
      context?.argument === 'reservesNationalGuardService'
    ) {
      return {
        chapterKey: 'veteranDetails',
        pageKey: isServingComponentGuardAndReserves(context?.formData)
          ? 'federalOrders'
          : 'obligationDates',
      };
    }

    const servicePayPage = getServicePayPage(error, context);
    if (servicePayPage) {
      return { chapterKey: ADDITIONAL_INFORMATION, pageKey: servicePayPage };
    }

    // A missing `hasNoAccount` is reported against the parent `directDeposit`
    // object, so exclude it to let it resolve to the directDeposit page.
    if (
      context?.argument !== 'hasNoAccount' &&
      BANK_ACCOUNT_ERROR_KEYS.some(key => error.split('.').includes(key))
    ) {
      return {
        chapterKey: 'additionalInformation',
        pageKey: 'paymentInformationReview',
      };
    }

    return null;
  },
};
